import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../components/CardList/CardListHeader';
import CardList from '../components/CardList/CardList';
import EditCardModal from '../components/CardList/EditCardModal';
import DeleteCardModals from '../components/CardList/DeleteCardModals';
import serverClient from '../api/ServerClient';
import { DeleteToken } from '../auth/Auth';
import { useNavigate } from 'react-router-dom';
import useOutletRefreshSignal from '../hooks/useOutletRefreshSignal';
import Alert from '../components/Alert';
import Loader from '../components/Loader';
import BottomNavbar from '../components/Home/BottomNavbar';
import { getPageMeta } from '../util/seoHelper';
import { useLanguage } from '../context/LanguageContext';
import useRefreshSignal from '../hooks/useRefreshSignal';

function Cards() {
    const navigate = useNavigate();
    const { language, t } = useLanguage();
    const [cards, setCards] = useState(null);
    const refreshSignal = useOutletRefreshSignal();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [deleteCardId, setDeleteCardId] = useState(null);
    const fetchCards = useCallback(async () => {
        try {
            setLoading(true);
            const response = await serverClient.get('/api/user/card');
            setCards(response?.data?.cards);
        } catch (e) {
            if (e.response?.status === 401) {
                DeleteToken();
                setError(t('userNotAuthorized'));
                navigate('/login');
            } else {
                setError(t('somethingWentWrong'));
            }
        } finally {
            setLoading(false);
        }
    }, [navigate, t]);

    useEffect(() => {
        fetchCards();
    }, [fetchCards]);

    useRefreshSignal(refreshSignal, () => {
        fetchCards();
    });

    const deleteCard = (card) => {
        setDeleteCardId(card.id);
    };

    const onCardAdd = async () => {
        fetchCards();
    };

    return (
        <div className="my-cards-page">
            <Helmet>
                <title>{getPageMeta('cards', language).title}</title>
                <meta name="description" content={getPageMeta('cards', language).description} />
                <meta name="keywords" content={getPageMeta('cards', language).keywords} />
                <link rel="canonical" href={getPageMeta('cards', language).url} />
                {getPageMeta('cards', language).noindex && (
                    <meta name="robots" content="noindex, follow" />
                )}

                {/* Open Graph Tags */}
                <meta property="og:type" content="website" />
                <meta property="og:title" content={getPageMeta('cards', language).title} />
                <meta
                    property="og:description"
                    content={getPageMeta('cards', language).description}
                />
                <meta property="og:url" content={getPageMeta('cards', language).url} />
                <meta property="og:image" content={getPageMeta('cards', language).image} />

                {/* Twitter Card Tags */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={getPageMeta('cards', language).title} />
                <meta
                    name="twitter:description"
                    content={getPageMeta('cards', language).description}
                />
                <meta name="twitter:image" content={getPageMeta('cards', language).image} />
            </Helmet>
            <Header showAdd={true} onCardAdd={onCardAdd} />
            {error && <Alert alert={'danger'}>{error}</Alert>}
            <Loader loading={loading} />
            {cards && <CardList cards={cards} onCardAdd={onCardAdd} deleteCard={deleteCard} />}
            <EditCardModal />
            <DeleteCardModals cardId={deleteCardId} onCardDelete={fetchCards} />
            <BottomNavbar />
        </div>
    );
}

export default Cards;
